import type { RouteLocationNormalized } from 'vue-router';
import { isForcingLogout, useAuth } from '~/composables/useAuth';
import { useCookieOptions } from '~/composables/useCookieOptions';
import { type AuthErrorCode, getAuthErrorCodes } from '~/constants/errorCodes';
import { AUTH_COOKIE_MAX_AGE, STORAGE_KEYS } from '~/constants/storageKeys';
import { useAuthStore } from '~/stores/auth';
import { useOrganizationStore } from '~/stores/organization';
import type { OrganizationsMember } from '~/types/organization';
import {
  DEVTOOLS_PROBE_PATH,
  ORG_SETUP_PATH,
  isCreateOrganizationIntent,
  isGuestOnlyPath,
  isOrgSetupFlowPath,
  isOrgSetupPath,
  isPublicPath,
  toComparablePath,
} from '~/utils/authRoutes';
import { hydrationSafeNavigateTo } from '~/utils/hydrationSafeNavigation';
import { useLocalePath } from '#i18n';

/**
 * 認證 / 組織的路由守衛
 *
 * 這裡是「使用者能不能待在這條路徑上」的唯一決策點。整個流程回答三個獨立的問題，
 * 依序往下走，每一層只做一件事：
 *
 *   1. 這是需要放行的特殊請求嗎？（DevTools 探測、OAuth 回調中繼）
 *   2. 有登入嗎？沒有 → 記住現在的位置，送去登入頁
 *   3. 組織就緒了嗎？沒有 → 送去組織設定頁；就緒了 → 把停在訪客頁的人送回 dashboard
 *
 * 決策一律以 `return navigate(...)` 或 `return true` 結束，不做副作用式的跳轉。
 */

// 不可寫進 redirect_after_login 的 query 參數
// 這份資料會被存進 cookie 與 localStorage 並在登入頁被讀回，
// 憑證類的值不應該以明文長期留在瀏覽器裡
const SENSITIVE_QUERY_KEYS = ['password', 'pwd', 'token', 'access_token', 'refresh_token'];

const omitSensitiveQuery = (query: Record<string, unknown>): Record<string, unknown> => {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(query)) {
    if (!SENSITIVE_QUERY_KEYS.includes(key.toLowerCase())) {
      result[key] = value;
    }
  }
  return result;
};

// ---------------------------------------------------------------------------
// 錯誤判讀
// ---------------------------------------------------------------------------

interface ApiError {
  status?: number;
  statusCode?: number;
  code?: number | string;
  message?: string;
}

const asApiError = (error: unknown): ApiError =>
  error && typeof error === 'object' ? (error as ApiError) : {};

const getErrorStatus = (error: unknown): number | undefined => {
  const apiError = asApiError(error);
  return apiError.status ?? apiError.statusCode;
};

const getErrorMessage = (error: unknown): string | undefined => {
  if (error instanceof Error) return error.message;
  return asApiError(error).message;
};

/** token 失效，還有機會靠 refresh 救回來 */
const isNotAuthenticatedError = (error: unknown): boolean =>
  error instanceof Error && error.message.includes('Not authenticated');

/**
 * 這個錯誤等同於「請重新登入」
 *
 * 401 是 access token 失效；422 則是後端拒絕了 refresh token
 * （回 VALIDATION_ERROR「刷新令牌已過期」）。兩者都代表 session 沒了，
 * 使用者該看到的是「登入失效」而不是「未預期錯誤」。
 */
const isAuthExpiredError = (error: unknown): boolean => {
  const status = getErrorStatus(error);
  return status === 401 || status === 422;
};

/**
 * 「目前不在任何組織中」
 * API 錯誤格式：{"code":400,"message":"Currently not in any organization","error":"bad_request"}
 * 錯誤可能把 400 放在 code / status / statusCode 任一欄位，三種都認。
 */
const isNoOrganizationError = (error: unknown): boolean => {
  const apiError = asApiError(error);
  const is400 =
    Number(apiError.code) === 400 || apiError.status === 400 || apiError.statusCode === 400;
  return is400 && getErrorMessage(error) === 'Currently not in any organization';
};

// ---------------------------------------------------------------------------
// 導航情境
// ---------------------------------------------------------------------------

/**
 * 從 URL 中手動解析 query 參數
 *
 * 不直接用 to.query 是因為部分 OAuth 回調會產生像 `?a=1?b=2` 這種多個 `?` 的異常格式，
 * Nuxt 會把後半整包當成單一參數的值。這裡先把第二個之後的 `?` 正規化成 `&` 再解析，
 * 最後才用 to.query 補上沒解析到的鍵。
 */
const parseQueryParams = (to: RouteLocationNormalized): Record<string, string> => {
  const params: Record<string, string> = {};

  if (import.meta.client && typeof window !== 'undefined' && window.location.search) {
    const normalizedSearch = window.location.search.replace(/\?(?=.*\?)/g, '&');
    try {
      for (const [key, value] of new URLSearchParams(normalizedSearch).entries()) {
        params[key] = value;
      }
    } catch (error) {
      console.warn('Failed to parse query params:', error);
    }
  }

  for (const [key, value] of Object.entries(to.query)) {
    if (value === null || value === undefined) continue;
    const stringValue = Array.isArray(value) ? value[0] : String(value);
    // 只有在手動解析沒有該參數時才使用 to.query 的值
    if (stringValue && !params[key]) {
      params[key] = stringValue;
    }
  }

  return params;
};

/** 單次導航的全部判斷材料，避免各層 guard 重複解析 */
interface GuardContext {
  to: RouteLocationNormalized;
  /** 去掉語系前綴、標準化後的路徑，可直接與 authRoutes 的常數比對 */
  path: string;
  queryParams: Record<string, string>;
  /** Google OAuth 的兩階段驗證回調 */
  is2FACallback: boolean;
  /** Google OAuth 帶回未註冊使用者，需要在登入頁補邀請碼 */
  isGuestUserCallback: boolean;
  /**
   * 取出上一頁的來源，用來決定 err_code 該回註冊頁還是登入頁。
   *
   * 讀了就銷毀（只用得到一次），但**只在真的要用時才讀**：過去是在建立 context 時
   * 無條件消費掉，於是 Google 登入回來的路上只要多經過一次導航，這個值就已經沒了，
   * 真正需要它的 `redirectForErrorCode` 永遠拿到空字串。
   */
  consumeFromUrl: () => string;
  /**
   * 用來在 await 之後恢復 Nuxt context
   *
   * useLocalePath / useCookie / useAuth 這類 composable 都需要 Nuxt instance，
   * 而 middleware 一旦 await 過（例如取組織資訊）就會離開 context，
   * 直接呼叫會拋 "A composable that requires access to the Nuxt instance..."。
   */
  nuxtApp: ReturnType<typeof useNuxtApp>;
}

const createGuardContext = (to: RouteLocationNormalized): GuardContext => {
  const queryParams = parseQueryParams(to);

  return {
    to,
    path: toComparablePath(to.path),
    queryParams,
    is2FACallback: queryParams.is_2fa_enabled === 'true' && !!queryParams.provisional_2fa_token,
    isGuestUserCallback: !!queryParams.guest_user_uuid,
    consumeFromUrl: () => {
      if (!import.meta.client) return '';
      const fromUrl = localStorage.getItem('from_url') || '';
      localStorage.removeItem('from_url');
      return fromUrl;
    },
    nuxtApp: useNuxtApp(),
  };
};

// ---------------------------------------------------------------------------
// 導向
// ---------------------------------------------------------------------------

/**
 * 這一層 guard 唯一的導向出口。
 *
 * 一律走 `hydrationSafeNavigateTo`：hydration 期間直接重導會讓 Vue 拿新頁的 vnode 樹
 * 去 hydrate 舊頁的 DOM，版面會爆掉而且舊頁的元件永遠不會 mount。
 * 完整的成因與症狀寫在 `utils/hydrationSafeNavigation.ts`。
 */
function navigate(
  ctx: GuardContext,
  to: Parameters<typeof navigateTo>[0],
  options?: Parameters<typeof navigateTo>[1]
) {
  return hydrationSafeNavigateTo(ctx.nuxtApp, to, options);
}

// ---------------------------------------------------------------------------
// 守衛
// ---------------------------------------------------------------------------

export default defineNuxtRouteMiddleware(async (to) => {
  // Chrome DevTools 的探測請求，直接放行
  if (to.path === DEVTOOLS_PROBE_PATH) {
    return true;
  }

  const ctx = createGuardContext(to);
  const authStore = useAuthStore();

  return authStore.isLoggedIn() ? await guardAuthenticated(ctx) : guardUnauthenticated(ctx);
});

/**
 * 未登入
 *
 * 公開頁一律放行；其餘先把目的地記下來，再依 query 決定送去註冊頁還是登入頁。
 */
function guardUnauthenticated(ctx: GuardContext) {
  const { to, path, queryParams } = ctx;
  const localePath = useLocalePath();
  const { saveRedirectUrl } = useAuth();

  if (isPublicPath(path)) {
    return true;
  }

  // 記住原始 URL 和 query 參數，以便登入後重定向
  const isRegisterFlow = (to.query.is_register as string) === 'false';
  saveRedirectUrl(
    isRegisterFlow ? localePath(`/${ORG_SETUP_PATH}`) : to.path,
    omitSensitiveQuery(to.query)
  );

  // is_register=false 代表使用者還沒註冊完，沿用原本邏輯導向註冊頁
  if (isRegisterFlow) {
    return navigate(ctx, { path: localePath('/signup'), query: to.query });
  }

  const errCode = to.query.err_code as unknown as AuthErrorCode;
  if (errCode) {
    return redirectForErrorCode(ctx, errCode);
  }

  // Google OAuth 帶回 guest_user_uuid，登入頁會據此開啟邀請碼 Modal
  if (ctx.isGuestUserCallback) {
    return navigate(ctx, {
      path: localePath('/login'),
      query: {
        guest_user_uuid: String(queryParams.guest_user_uuid),
        ...(queryParams.invite_code ? { invite_code: String(queryParams.invite_code) } : {}),
      },
    });
  }

  // 使用 navigateTo 而不是 window.location.href，以保留 query 參數
  return navigate(ctx, localePath('/login'));
}

/** err_code 決定該回哪個認證頁 */
function redirectForErrorCode(ctx: GuardContext, errCode: AuthErrorCode) {
  const { to } = ctx;
  const localePath = useLocalePath();

  const errCodeInfo = getAuthErrorCodes(errCode);
  if (errCodeInfo) {
    return navigate(ctx, localePath(`/${errCodeInfo.redirectType}?err_code=${errCode}`));
  }

  if (ctx.consumeFromUrl().toLowerCase().includes('signup')) {
    return navigate(ctx, localePath(`/signup?err_code=${errCode}`));
  }

  // 保留 guest_user_uuid 和其他 query 參數
  const query: Record<string, string> = { err_code: String(errCode) };
  if (to.query.guest_user_uuid) query.guest_user_uuid = String(to.query.guest_user_uuid);
  if (to.query.invite_code) query.invite_code = String(to.query.invite_code);
  return navigate(ctx, { path: localePath('/login'), query });
}

/**
 * 已登入
 *
 * 先讓 OAuth 回調中繼通過（此時組織狀態還沒意義），再依組織是否就緒決定去向。
 */
async function guardAuthenticated(ctx: GuardContext) {
  const { path } = ctx;

  // OAuth 回調正在登入頁完成收尾（2FA 輸入 / 邀請碼 Modal），先放行
  if (path === 'login' && (ctx.is2FACallback || ctx.isGuestUserCallback)) {
    return true;
  }

  // 取組織資訊是唯一的 await；之後的分支會用到 useLocalePath / useCookie / useAuth，
  // 必須用 runWithContext 包起來，否則會離開 Nuxt context 而拋錯
  try {
    const currentOrg = await useOrganizationStore().getCurrentOrganization();
    return await ctx.nuxtApp.runWithContext(() => resolveOrganizationState(ctx, currentOrg));
  } catch (error) {
    return await ctx.nuxtApp.runWithContext(() => handleOrganizationError(ctx, error));
  }
}

/** 依「組織是否就緒」決定去向 */
function resolveOrganizationState(ctx: GuardContext, currentOrg: OrganizationsMember | null) {
  const { to, path } = ctx;
  const localePath = useLocalePath();

  // 還沒有組織，或有組織但尚未切換成當前組織 → 只能待在設定流程裡
  // （用 isOrgSetupFlowPath 而不是 isOrgSetupPath：付款完成的處理中頁也算，
  //   否則剛付完款回站的人會被踢回設定頁的第一步）
  if (!currentOrg?.is_current) {
    return isOrgSetupFlowPath(path)
      ? true
      : navigate(ctx, { path: localePath(`/${ORG_SETUP_PATH}`), query: to.query });
  }

  // 組織就緒，記錄下來給 cookie 遷移驗證 token 的組織 scope 用
  // （見 plugins/00.authCookieMigration.client.ts）。
  // maxAge 要跟 token 一致：留成 session cookie 的話，關掉瀏覽器再開就驗不了 scope。
  if (import.meta.client) {
    useCookie(STORAGE_KEYS.ORGUUID, useCookieOptions({ maxAge: AUTH_COOKIE_MAX_AGE })).value =
      currentOrg.organizations_uuid;
  }

  // 2FA 回調要回登入頁完成驗證
  if (ctx.is2FACallback) {
    return navigate(ctx, {
      path: localePath('/login'),
      query: {
        is_2fa_enabled: to.query.is_2fa_enabled,
        provisional_2fa_token: to.query.provisional_2fa_token,
      },
    });
  }

  // 已有組織的人主動要再開一個組織：設定頁正是他要去的地方，放行
  if (isOrgSetupPath(path) && isCreateOrganizationIntent(to.query)) {
    return true;
  }

  // 已經登入且組織就緒，就不該再停在登入／註冊／忘記密碼，也不該停在組織設定頁
  if (isGuestOnlyPath(path) || isOrgSetupPath(path)) {
    const target = useAuth().consumeRedirectUrl();
    return target
      ? navigate(ctx, { path: target.path, query: target.query })
      : navigate(ctx, localePath('/dashboard'));
  }

  // 正常訪問
  return true;
}

/** 取組織資訊失敗時的分流 */
async function handleOrganizationError(ctx: GuardContext, error: unknown) {
  const { to, path } = ctx;
  const localePath = useLocalePath();

  // token 失效但還有 refresh token，換一張再讓這次導航過去。
  // 只在瀏覽器端做：伺服器端換到的新 token 送不回瀏覽器，卻已經把一次性的 refresh token
  // 消費掉（詳見 useAuth.refreshTokenOnUnauthorized），下面的 import.meta.server 分支會接手。
  if (import.meta.client && isNotAuthenticatedError(error)) {
    await useAuth().refreshTokenOnUnauthorized();
    return true;
  }

  // 後端明確回報「不在任何組織中」，等同於沒有組織
  if (isNoOrganizationError(error)) {
    return isOrgSetupFlowPath(path)
      ? true
      : navigate(
          ctx,
          { path: localePath(`/${ORG_SETUP_PATH}`), query: to.query },
          { replace: true }
        );
  }

  // 組織設定流程本來就是用來處理「沒有組織」的，留在原地避免循環重定向
  if (isOrgSetupFlowPath(path)) {
    return true;
  }

  // 認證相關頁面也不再導向，同樣是為了避免循環
  if (isPublicPath(path)) {
    return true;
  }

  // 已經有人在跑強制登出（useApiFetch 的 401 流程）：那邊會自己整頁跳轉並顯示「登入失效」。
  // 這裡不要再導一次——此時手上的 error 往往只是被跳轉中斷的請求，會把文案蓋成「未預期錯誤」。
  if (isForcingLogout()) {
    return true;
  }

  // SSR 階段拿不到組織資訊，不代表使用者真的沒登入，不要在這裡把人送去登入頁。
  //
  // 伺服器端就算想續期也救不回來：換到的新 token 不一定回得到瀏覽器，
  // 而 refresh token 是一次性的，反而可能把瀏覽器手上那張消費掉。
  // 實際觀察到的就是 /dashboard 被 302 到 /login，瀏覽器端 refresh 成功後又被導回 dashboard，
  // 使用者看到登入頁閃一下。
  //
  // 放行交給瀏覽器端：hydrate 之後 API 層本來就會自己 refresh 並重試，
  // 真的失效時 useApiFetch 的 401 流程會顯示「登入失效」並整頁跳轉。
  if (import.meta.server) {
    console.warn('[auth] 伺服器端取組織資訊失敗，交給瀏覽器端處理：', {
      path,
      status: getErrorStatus(error),
      message: getErrorMessage(error),
    });
    return true;
  }

  // 其他錯誤送回登入頁，並用 query 告訴登入頁該顯示哪種訊息
  return navigate(ctx, {
    path: localePath('/login'),
    query: {
      ...to.query,
      authError: isAuthExpiredError(error) ? 'loginExpired' : 'unexpectedError',
    },
  });
}
